import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { HoverInfo } from "../types";

interface UmapDataPoint {
  [key: string]: any;
}

interface TooltipFieldConfig {
  label: string;
  key: string;
}

interface UmapTooltipProps<T extends UmapDataPoint> {
  info: HoverInfo;
  hoveredObject: T;
  fields?: TooltipFieldConfig[];
  showImage?: boolean;
  svgDirectory?: string;
}

const UmapTooltip = <T extends UmapDataPoint>({
  info,
  hoveredObject,
  fields,
  showImage = true,
  svgDirectory = "svgDirectory",
}: UmapTooltipProps<T>) => {
  const tooltipRef = useRef<HTMLDivElement>(null);

  const [position, setPosition] = useState<{
    left: number;
    top: number;
    visible: boolean;
  }>({
    left: 0,
    top: 0,
    visible: false,
  });

  const imagePath = hoveredObject[svgDirectory];

  useEffect(() => {
    if (hoveredObject && tooltipRef.current) {
      const { x, y } = info;
      const tooltipElement = tooltipRef.current;

      tooltipElement.style.whiteSpace = "nowrap";
      const tooltipWidth = tooltipElement.offsetWidth;
      const tooltipHeight = tooltipElement.offsetHeight;
      tooltipElement.style.whiteSpace = "normal";

      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const offset = 15;

      let finalLeft = x + offset;
      let finalTop = y + offset;

      if (finalLeft + tooltipWidth > viewportWidth) {
        finalLeft = x - offset - tooltipWidth;
      }

      if (finalLeft < 0) {
        finalLeft = offset;
      }

      if (finalTop + tooltipHeight > viewportHeight) {
        finalTop = y - offset - tooltipHeight;
      }

      if (finalTop < 0) {
        finalTop = offset;
      }

      setPosition({ left: finalLeft, top: finalTop, visible: true });
    } else {
      setPosition((p) => ({ ...p, visible: false }));
    }
  }, [info, hoveredObject]);

  if (!hoveredObject) {
    return null;
  }

  const defaultFields: TooltipFieldConfig[] = fields || [
    { label: "LNP ID", key: "lnpId" },
    { label: "Library Code", key: "experimentId" },
    { label: "IL Name", key: "ilName" },
    { label: "IL Molratio", key: "ilMolratio" },
    { label: "HL Name", key: "hlName" },
    { label: "HL Molratio", key: "hlMolratio" },
    { label: "CHL Molratio", key: "chlMolratio" },
    { label: "PEG Name", key: "pegName" },
    { label: "PEG Molratio", key: "pegMolratio" },
    { label: "Cargo", key: "cargo" },
    { label: "Experiment Method", key: "experimentMethod" },
  ];

  const tooltipClasses = clsx(
    "absolute z-10 pointer-events-none",
    "bg-white p-3 rounded shadow-lg text-xs",
    "flex flex-col",
    "transition-opacity duration-150 ease-in-out",
    position.visible ? "opacity-100 visible" : "opacity-0 invisible",
    showImage ? "max-w-[400px]" : "max-w-[300px]"
  );

  return (
    <div
      ref={tooltipRef}
      className={tooltipClasses}
      style={{
        left: `${position.left}px`,
        top: `${position.top}px`,
      }}
    >
      <div className="flex-1">
        {defaultFields.map(
          (field) =>
            hoveredObject[field.key] != null &&
            hoveredObject[field.key] !== "" && (
              <div key={field.key}>
                <b>{field.label}:</b> {hoveredObject[field.key]}
              </div>
            )
        )}
      </div>

      {showImage && imagePath && (
        <div className="mt-2.5 text-center max-w-full overflow-hidden max-h-[320px]">
          <img
            src={`/ionizableLipid/LNP_${String(hoveredObject["index"]).padStart(
              7,
              "0"
            )}.svg`}
            alt={hoveredObject["index"]}
            className="object-contain max-w-full max-h-[300px]"
          />
        </div>
      )}
    </div>
  );
};

export default UmapTooltip;
